import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Team, TeamMatch } from '../services/api';

const TeamDetail: React.FC = () => {
  const { year, group, teamId } = useParams<{ year: string; group: string; teamId: string }>();
  const [team, setTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Logo handling (same as Schedule component)
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());
  
  const getTeamLogoUrl = (teamId: string): string => {
    return `https://pub-e0ccb659989a49a297dc22911f822b08.r2.dev/photos/teams/logos/${teamId}.png`;
  };
  
  const handleLogoError = (teamId: string) => {
    setFailedLogos(prev => new Set(Array.from(prev).concat(teamId)));
  };
  
  const logoFailed = (teamId: string): boolean => {
    return failedLogos.has(teamId);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!teamId || !year || !group) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const teamData = await apiService.getTeam(teamId);
        setTeam(teamData);
      } catch (err) {
        setError('Failed to load team data');
        console.error('Error fetching team data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [teamId, year, group]);

  if (isLoading) return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>{error}</div>;
  if (!team) return <div style={{ textAlign: 'center', padding: '20px' }}>Team not found</div>;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const getResultStatus = (match: TeamMatch) => {
    if (!match.result) return null;
    return match.result.startsWith('W') ? 'W' : 'L';
  };

  // Sort players by jersey number: 0 first, then numbers ascending, then '-'
  const sortPlayersByJersey = (players: any[]) => {
    return [...players].sort((a, b) => {
      let jerseyA = a.jersey;
      let jerseyB = b.jersey;
      
      // Handle null/undefined jersey numbers
      if (jerseyA === null || jerseyA === undefined) jerseyA = '-';
      if (jerseyB === null || jerseyB === undefined) jerseyB = '-';
      
      // Convert to numbers for comparison
      const numA = jerseyA === '-' ? Infinity : Number(jerseyA);
      const numB = jerseyB === '-' ? Infinity : Number(jerseyB);
      
      // Special case: 0 should come first
      if (numA === 0 && numB !== 0) return -1;
      if (numB === 0 && numA !== 0) return 1;
      
      // Then sort by numeric value
      return numA - numB;
    });
  };

  return (
    <div className="modern-container">
      <div className="modern-page-card">
        {/* Navigation */}
        <div style={{ marginBottom: '30px' }}>
          <Link 
            to={`/summer-league/${year}/${group}/teams`}
            style={{ 
              color: '#1976d2', 
              textDecoration: 'none', 
              fontSize: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 600
            }}
          >
            ← 返回
          </Link>
        </div>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '10px', color: '#0f172a', fontWeight: 800 }}>{team.name}</h1>
          <h2 style={{ fontSize: '1.2rem', color: '#64748b', marginBottom: '15px' }}>
            Summer League {year} - Group {group}
          </h2>
        </div>

        {/* Team Logo and Photo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Team Logo */}
            <div>
              {!logoFailed(team.id) ? (
                <img 
                  src={getTeamLogoUrl(team.id)} 
                  alt={`${team.name} Logo`}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                    padding: 8,
                    border: '1px solid #edf2f7'
                  }}
                  onError={() => {
                    handleLogoError(team.id);
                  }}
                />
              ) : (
                <div style={{
                  width: '150px',
                  height: '150px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '60px',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)'
                }}>
                  {team.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Team Photo */}
            {team.teamphoto && (
              <div>
                <img 
                  src={team.teamphoto} 
                  alt={`${team.name} Team Photo`}
                  style={{
                    width: '240px',
                    height: '160px',
                    borderRadius: '16px',
                    objectFit: 'cover',
                    border: '1px solid #edf2f7',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)'
                  }}
                />
                <p style={{ marginTop: '10px', fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Team Photo</p>
              </div>
            )}
          </div>
        </div>

        {/* Coaching Staff and Jersey Color Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '40px' }}>
          {/* Coaching Staff */}
          <div className="modern-section-card" style={{ marginBottom: 0 }}>
            <h3 className="modern-section-title">教練團隊</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <h4 style={{ fontSize: '1rem', marginBottom: '4px', color: '#64748b', fontWeight: 600 }}>主教練:</h4>
                <p style={{ fontSize: '16px', color: '#1e293b', margin: '0', fontWeight: 700 }}>{team.coach1}</p>
              </div>
              {team.coach2 && (
                <div>
                  <h4 style={{ fontSize: '1rem', marginBottom: '4px', color: '#64748b', fontWeight: 600 }}>助教:</h4>
                  <p style={{ fontSize: '16px', color: '#1e293b', margin: '0', fontWeight: 700 }}>{team.coach2}</p>
                </div>
              )}
            </div>
          </div>

          {/* Jersey Color */}
          {team.jerseycolor && (
            <div className="modern-section-card" style={{ marginBottom: 0 }}>
              <h3 className="modern-section-title">球衣顏色</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', height: '100%', paddingBottom: '16px', boxSizing: 'border-box' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: team.jerseycolor || '#ccc',
                  borderRadius: '8px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                }}></div>
                <span style={{ fontSize: '18px', color: '#1e293b', fontWeight: 700 }}>{team.jerseycolor}</span>
              </div>
            </div>
          )}
        </div>

        {/* Players Roster */}
        <div className="modern-section-card">
          <h3 className="modern-section-title">球員名單</h3>
          {team.players && team.players.length > 0 ? (
            <div className="modern-table-wrapper">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th className="align-left">球員名稱</th>
                    <th>身高</th>
                    <th>位置</th>
                    <th>球衣編號</th>
                  </tr>
                </thead>
                <tbody>
                  {sortPlayersByJersey(team.players).map((player) => (
                    <tr key={player.id}>
                      <td className="align-left">
                        <Link to={`/players/${player.id}`} className="modern-link">
                          {player.name}
                        </Link>
                      </td>
                      <td>{player.height ? `${player.height}cm` : '-'}</td>
                      <td>{player.position || '-'}</td>
                      <td style={{ fontWeight: 600 }}>
                        {player.jersey !== null && player.jersey !== undefined ? player.jersey : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              No players found for this team
            </div>
          )}
        </div>

        {/* Match Schedule */}
        <div className="modern-section-card">
          <h3 className="modern-section-title">比賽成績</h3>
          {team.matches && team.matches.length > 0 ? (
            <div style={{ display: 'grid', gap: '20px' }}>
              {team.matches.map((match) => (
                <Link 
                  key={match.id} 
                  to={`/summer-league/matches/${match.id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div 
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '16px',
                      padding: '20px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    {/* Match Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                        場次 {match.match_no}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                        {formatDate(match.date)} {match.time && formatTime(match.time)}
                      </div>
                    </div>
                    
                    {/* Match Content */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {/* Our Team */}
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ marginBottom: '8px' }}>
                          {!logoFailed(team.id) ? (
                            <img 
                              src={getTeamLogoUrl(team.id)} 
                              alt={`${team.name} Logo`}
                              style={{
                                maxWidth: '55px',
                                maxHeight: '55px',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                backgroundColor: '#ffffff',
                                margin: '0 auto',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                padding: 2
                              }}
                              onError={() => {
                                handleLogoError(team.id);
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '55px',
                              height: '55px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px',
                              color: '#64748b',
                              border: '1px solid #e2e8f0',
                              margin: '0 auto'
                            }}>
                              {team.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                          {team.name}
                        </div>
                      </div>
                      
                      {/* VS and Venue */}
                      <div style={{ textAlign: 'center', margin: '0 20px', minWidth: '120px' }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '4px', color: '#94a3b8' }}>
                          VS
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', minHeight: '16px' }}>
                          {match.venue ? `📍 ${match.venue}` : ''}
                        </div>
                        <div style={{ 
                          fontSize: '1.6rem', 
                          fontWeight: '800', 
                          marginTop: '8px',
                          color: getResultStatus(match) === 'W' ? '#10b981' : '#ef4444',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {match.result || ''}
                        </div>
                      </div>
                      
                      {/* Opponent Team */}
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ marginBottom: '8px' }}>
                          {('opponent_id' in match) && !failedLogos.has((match as any).opponent_id) ? (
                            <img
                              src={getTeamLogoUrl((match as any).opponent_id)}
                              alt={`${match.opponent} Logo`}
                              style={{
                                maxWidth: '55px',
                                maxHeight: '55px',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                backgroundColor: '#ffffff',
                                margin: '0 auto',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                padding: 2
                              }}
                              onError={() => {
                                handleLogoError((match as any).opponent_id);
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '55px',
                              height: '55px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px',
                              color: '#64748b',
                              border: '1px solid #e2e8f0',
                              margin: '0 auto'
                            }}>
                              {match.opponent.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                          {match.opponent}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              No matches found for this team
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDetail;